import {
  Box,
  Button,
  Flex,
  Text,
  useColorModeValue,
  VStack,
  HStack,
  Textarea,
  Select,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Card,
  Stepper,
  Step,
  StepDescription,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  useSteps,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateFaqMutation } from 'api/faqsSlice';

const steps = [
  { title: 'Basic Information', description: 'Question and Answer' },
  { title: 'Preview', description: 'Review and Create' },
];

const AddFaq = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { activeStep, setActiveStep } = useSteps({
    index: 0,
    count: 2,
  });

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  // API hooks
  const [createFaq, { isLoading: isCreating }] = useCreateFaqMutation();

  const [formData, setFormData] = React.useState({
    question: '',
    answer: '',
  });

  const [errors, setErrors] = React.useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0:
        if (!formData.question.trim()) {
          newErrors.question = 'Question is required';
        }
        if (!formData.answer.trim()) {
          newErrors.answer = 'Answer is required';
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(activeStep + 1);
    }
  };

  const handlePrevious = () => {
    setActiveStep(activeStep - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(0)) {
      return;
    }

    try {
      // Prepare the data according to the API structure
      const faqData = {
        question: formData.question,
        answer: formData.answer,
      };

      await createFaq(faqData).unwrap();

      toast({
        title: 'Success',
        description: 'FAQ created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate('/admin/undefined/faqs');
      
    } catch (error) {
      console.error('Failed to create FAQ:', error);
      toast({
        title: 'Error',
        description: error.data?.message || 'Failed to create FAQ. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <VStack spacing={6} align="stretch" w="100%">
            <FormControl isInvalid={!!errors.question}>
              <FormLabel color={textColor} fontWeight="bold">
                Question *
              </FormLabel>
              <Textarea
                placeholder="Enter the frequently asked question..."
                value={formData.question}
                onChange={(e) => handleInputChange('question', e.target.value)}
                rows={3}
                resize="vertical"
              />
              <FormErrorMessage>{errors.question}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.answer}>
              <FormLabel color={textColor} fontWeight="bold">
                Answer *
              </FormLabel>
              <Textarea
                placeholder="Enter the detailed answer..."
                value={formData.answer}
                onChange={(e) => handleInputChange('answer', e.target.value)}
                rows={6}
                resize="vertical"
              />
              <FormErrorMessage>{errors.answer}</FormErrorMessage>
            </FormControl>
          </VStack>
        );

      case 1:
        return (
          <VStack spacing={6} align="stretch" w="100%">
            <Box p={6} bg="gray.50" borderRadius="md" border="1px solid" borderColor={borderColor}>
              <Text fontSize="lg" fontWeight="bold" color={textColor} mb={4}>
                Preview FAQ
              </Text>
              
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontSize="sm" color="gray.500" mb={1}>
                    Question:
                  </Text>
                  <Text fontWeight="bold" color={textColor} fontSize="md">
                    {formData.question || 'No question entered yet...'}
                  </Text>
                </Box>
                
                <Box>
                  <Text fontSize="sm" color="gray.500" mb={1}>
                    Answer:
                  </Text>
                  <Text color={textColor} fontSize="sm" lineHeight="1.6">
                    {formData.answer || 'No answer entered yet...'}
                  </Text>
                </Box>
              </VStack>
            </Box>

            <Box p={4} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
              <Text fontSize="sm" color="blue.700">
                <strong>Ready to create:</strong> Review the information above and click "Create FAQ" to add this FAQ to your system.
              </Text>
            </Box>
          </VStack>
        );

      default:
        return null;
    }
  };

  return (
    <Box className="nr-page">
      <Card p={6}>
        <VStack spacing={8} align="stretch">
          <Box>
            <Text color={textColor} fontSize="2xl" fontWeight="bold" mb={2}>
              Add New FAQ
            </Text>
            <Text color="gray.500">
              Create a new frequently asked question with detailed information
            </Text>
          </Box>

          <Stepper index={activeStep} orientation="horizontal" size="lg">
            {steps.map((step, index) => (
              <Step key={index}>
                <StepIndicator>
                  <StepStatus
                    complete={<StepIcon />}
                    incomplete={<StepNumber />}
                    active={<StepNumber />}
                  />
                </StepIndicator>

                <Box flexShrink="0">
                  <StepTitle>{step.title}</StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                </Box>

                <StepSeparator />
              </Step>
            ))}
          </Stepper>

          <Box p={6} border="1px solid" borderColor={borderColor} borderRadius="lg">
            {renderStepContent()}
          </Box>

          <HStack justify="space-between">
            <Button
              onClick={() => navigate('/admin/faqs')}
              variant="outline"
              colorScheme="gray"
            >
              Cancel
            </Button>

            <HStack>
              {activeStep > 0 && (
                <Button
                  onClick={handlePrevious}
                  variant="outline"
                  colorScheme="blue"
                >
                  Previous
                </Button>
              )}

              {activeStep < steps.length - 1 ? (
                <Button
                  onClick={handleNext}
                  colorScheme="blue"
                >
                  Next
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  colorScheme="green"
                  isLoading={isCreating}
                  loadingText="Creating FAQ"
                >
                  Create FAQ
                </Button>
              )}
            </HStack>
          </HStack>
        </VStack>
      </Card>
    </Box>
  );
};

export default AddFaq;
